import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { Building2, Pill, User } from 'lucide-react';

// Linear / Vercel inspired palette — subtle neutrals + accent
const PALETTE = {
  traders:   '#3b82f6', // blue 500
  pharmacy:  '#10b981', // emerald 500
  ayush:     '#8b5cf6', // violet 500
  bharti:    '#ec4899', // pink 500
  other:     '#64748b', // slate 500
};

const CATEGORY_LABELS = {
  traders: 'Traders',
  pharmacy: 'Pharmacy',
  ayush: 'Ayush',
  bharti: 'Bharti',
  other: 'Other',
};

const CATEGORY_ICONS = {
  traders: Building2,
  pharmacy: Pill,
  ayush: User,
  bharti: User,
};

const formatINR = (n) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', minimumFractionDigits: 0,
}).format(n || 0);

const formatCompact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(1)}k`;
  return `₹${v.toFixed(0)}`;
};

const monthShort = (key) => {
  // key: YYYY-MM
  if (!key) return '';
  const [y, m] = key.split('-');
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'short' });
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const value = payload[0].value;
  return (
    <div className="rounded-md border border-zinc-200 bg-white/95 backdrop-blur px-2.5 py-1.5 shadow-sm">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label || payload[0].name}</div>
      <div className="text-sm font-semibold text-zinc-900 tabular-nums">{formatINR(value)}</div>
    </div>
  );
};

export const DonutBreakdown = ({ report, expenses }) => {
  const [mode, setMode] = useState('category'); // 'category' | 'firm'

  const data = useMemo(() => {
    if (mode === 'firm') {
      // Group by firm: pharmacy = pharmacy expenses; traders = traders + ayush + bharti
      const pharmacy = report?.category_totals?.pharmacy || 0;
      const traders = ['traders', 'ayush', 'bharti'].reduce(
        (s, k) => s + (report?.category_totals?.[k] || 0), 0
      );
      return [
        { name: 'Pharmacy', key: 'pharmacy', value: pharmacy, color: '#10b981' },
        { name: 'Traders',  key: 'traders',  value: traders,  color: '#3b82f6' },
      ].filter(d => d.value > 0);
    }
    const cats = report?.category_totals || {};
    return Object.entries(cats)
      .filter(([, v]) => v > 0)
      .map(([key, value]) => ({
        name: CATEGORY_LABELS[key] || key,
        key,
        value,
        color: PALETTE[key] || PALETTE.other,
      }));
  }, [mode, report]);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="expense-donut-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">Breakdown</h3>
          <p className="text-xs text-zinc-500">By {mode === 'category' ? 'category' : 'firm'}</p>
        </div>
        <div className="inline-flex rounded-md border border-zinc-200 p-0.5 bg-zinc-50">
          <button
            data-testid="donut-mode-category"
            onClick={() => setMode('category')}
            className={`px-2.5 py-1 text-xs rounded-[5px] transition-colors ${
              mode === 'category' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >Category</button>
          <button
            data-testid="donut-mode-firm"
            onClick={() => setMode('firm')}
            className={`px-2.5 py-1 text-xs rounded-[5px] transition-colors ${
              mode === 'firm' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >Firm</button>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="h-[200px] flex items-center justify-center text-xs text-zinc-400">
          No data for this month
        </div>
      ) : (
        <div className="grid grid-cols-5 gap-3 items-center">
          <div className="col-span-2 relative h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  innerRadius={48}
                  outerRadius={72}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                >
                  {data.map((d) => <Cell key={d.key} fill={d.color} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[10px] uppercase tracking-wide text-zinc-400">Total</span>
              <span className="text-sm font-semibold tabular-nums text-zinc-900">{formatCompact(total)}</span>
            </div>
          </div>
          <div className="col-span-3 space-y-1.5">
            {data.map((d) => {
              const pct = total > 0 ? (d.value / total) * 100 : 0;
              return (
                <div key={d.key} className="flex items-center gap-2 text-xs">
                  <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }} />
                  <span className="text-zinc-600 flex-1 truncate">{d.name}</span>
                  <span className="text-zinc-400 tabular-nums w-10 text-right">{pct.toFixed(0)}%</span>
                  <span className="text-zinc-900 font-medium tabular-nums w-16 text-right">{formatCompact(d.value)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const TrendChart = ({ series }) => {
  const data = useMemo(
    () => (series || []).map(s => ({ month: monthShort(s.month), total: s.total, key: s.month })),
    [series]
  );
  const latest = data[data.length - 1]?.total || 0;
  const prev = data[data.length - 2]?.total || 0;
  const delta = prev > 0 ? ((latest - prev) / prev) * 100 : 0;
  const up = delta >= 0;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="expense-trend-card">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">12-month trend</h3>
          <p className="text-xs text-zinc-500">Total monthly spend</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold tabular-nums text-zinc-900">{formatCompact(latest)}</div>
          <div className={`text-[10px] font-medium ${up ? 'text-rose-600' : 'text-emerald-600'}`}>
            {up ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}% MoM
          </div>
        </div>
      </div>
      <div className="h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatCompact}
              width={48}
            />
            <Tooltip content={<ChartTooltip />} />
            <Area
              type="monotone"
              dataKey="total"
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#trendFill)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Tiny sparkline for summary cards (last 6 months for one category)
export const Sparkline = ({ data, color = '#3b82f6' }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.v), 1);
  const min = Math.min(...data.map(d => d.v), 0);
  const range = max - min || 1;
  const w = 80;
  const h = 24;
  const step = data.length > 1 ? w / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = i * step;
    const y = h - ((d.v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  const area = `0,${h} ${points} ${w},${h}`;
  const gradId = `spark-${color.replace('#', '')}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="overflow-visible">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <polygon fill={`url(#${gradId})`} points={area} />
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" points={points} />
    </svg>
  );
};

export { CATEGORY_LABELS, CATEGORY_ICONS, PALETTE, formatINR, formatCompact };
